import React from 'react';
import { Badge, Text, Tooltip } from '@mantine/core';
import { isIsoDateTime } from '../../utils/entityPropertyUtils';
import { formatDateTime } from '../../utils/dateTimeUtils';
import type { EntityRegistryEntry, ColumnOptions } from '../types';

export const spooledEmailConfig: EntityRegistryEntry = {
    hideIdInStandard: true,
    fetchAttributes: ['Id', 'DisplayName', 'EntityType', 'Status', 'From', 'To', 'SendAttempts', 'Recorded', 'LastSendAttempt'],
    tableFlags: { sortingDisabled: true },
    listColumns: ({ isStandard }: ColumnOptions) => {
        const columns = [
            {
                key: 'status',
                label: 'Status',
                sortable: false,
                render: (entity: any) => {
                    const status = (entity.Status || '').toString().trim();
                    if (!status) return '';
                    const colorMap: Record<string, string> = { Queued: 'yellow', Sent: 'green' };
                    const color = colorMap[status] ?? 'gray';
                    return <Badge variant="light" size="sm" color={color}>{status}</Badge>;
                }
            },
            {
                key: 'from',
                label: 'From',
                sortable: false,
                render: (entity: any) => {
                    const from = (entity.From || '').toString().trim();
                    if (!from) return '';
                    return from.length > 30 ? `${from.slice(0, 27)}...` : from;
                }
            },
            {
                key: 'to',
                label: 'To',
                sortable: false,
                render: (entity: any) => {
                    let recipients: string[] = [];
                    const raw = entity.To;
                    if (Array.isArray(raw)) {
                        recipients = raw.filter(Boolean);
                    } else if (typeof raw === 'string') {
                        try { recipients = JSON.parse(raw).filter(Boolean); } catch { recipients = raw ? [raw] : []; }
                    }
                    if (recipients.length === 0) return '';
                    const first = recipients[0].length > 25 ? `${recipients[0].slice(0, 22)}...` : recipients[0];
                    if (recipients.length === 1) return first;
                    return (
                        <Tooltip label={recipients.join(', ')}>
                            <Text size="sm" component="span">{first} <Badge variant="light" size="xs" color="gray">+{recipients.length - 1}</Badge></Text>
                        </Tooltip>
                    );
                }
            },
            {
                key: 'sendattempts',
                label: 'Attempts',
                sortable: false,
                render: (entity: any) => {
                    const attempts = entity.SendAttempts;
                    if (attempts == null || attempts === '' || attempts === 0) return '';
                    const status = (entity.Status || '').toString().trim();
                    const isRetrying = attempts > 0 && status === 'Queued';
                    return isRetrying
                        ? <Badge variant="light" size="sm" color="red">{attempts}</Badge>
                        : <Text size="sm">{attempts}</Text>;
                }
            },
            {
                key: 'recorded',
                label: 'Recorded',
                sortable: false,
                render: (entity: any) => {
                    const recorded = entity.Recorded || entity.recorded;
                    if (!recorded) return '';
                    if (typeof recorded === 'string' && isIsoDateTime(recorded)) {
                        return <span title={recorded}>{formatDateTime(recorded)}</span>;
                    }
                    return recorded;
                }
            }
        ];

        if (!isStandard) {
            columns.push({
                key: 'lastsendattempt',
                label: 'Last Attempt',
                sortable: false,
                render: (entity: any) => {
                    const val = entity.LastSendAttempt;
                    if (!val) return '';
                    if (typeof val === 'string' && isIsoDateTime(val)) {
                        return <span title={val}>{formatDateTime(val)}</span>;
                    }
                    return val;
                }
            });
        }

        return columns;
    }
};
